/**
 * Cash Flow Calculator - DRF management and sequential cash flow processing
 *
 * This module handles:
 * - DRF (Debt Reserve Fund) balance management and sequential processing
 * - Cash flow completion calculations
 * - DRF constraint validation (negative balances, minimum balance violations)
 * - Sequential dependency management (each payment depends on previous)
 *
 * Ported from engine-prototype's cashflow-calculator.ts
 */

import type { Candidate, CashFlow, Configuration } from '~/types';
import { parseNumericInput } from '~/utils/numberParsing';
import {
  calculateDrfAdjustment,
  calculateDrfEndingBalance,
  calculateDrfNetChange,
  calculateDrfNetRevenue,
} from '../../calculators/cashflow/drf-calculations';
import type { DRFCalculationInputs, DRFPeriodResult, DRFPlan, DRFViolation } from './drf-types';

// ============================================================================
// PURE DRF CALCULATION (Phase 6)
// ============================================================================

/**
 * Pure function that calculates the complete DRF plan.
 *
 * This function computes DRF balances for all payment periods without
 * mutating any inputs. Each period's starting balance depends on the
 * previous period's ending balance, creating a sequential dependency.
 *
 * @param cashFlows - Readonly cash flow records (revenue and payment data)
 * @param inputs - DRF configuration (starting balance, minimum balance)
 * @returns DRFPlan with all period results and any violations
 *
 * @pure No side effects, deterministic output
 */
export function calculateDRFPlan(
  cashFlows: readonly CashFlow[],
  inputs: DRFCalculationInputs
): DRFPlan {
  const { startingBalance, minimumBalance } = inputs;

  if (cashFlows.length === 0) {
    return {
      periods: [],
      violations: [],
      isViable: true,
      summary: {
        startingBalance,
        minimumBalance,
        finalBalance: startingBalance,
        lowestBalance: startingBalance,
        highestBalance: startingBalance,
      },
    };
  }

  const periods: DRFPeriodResult[] = [];
  const violations: DRFViolation[] = [];

  for (let i = 0; i < cashFlows.length; i++) {
    const cf = cashFlows[i];
    const previousPeriod = i > 0 ? periods[i - 1] : null;

    // Calculate DRF values for this period
    const periodStartingBalance = previousPeriod?.endingBalance ?? startingBalance;

    const adjustment = calculateDrfAdjustment(cf.revenueMinusExistingDebt, cf.totalBondPayment);

    const endingBalance = calculateDrfEndingBalance(periodStartingBalance, adjustment);
    const netChange = calculateDrfNetChange(adjustment);
    const netRevenue = calculateDrfNetRevenue(cf.paymentRevenuePortion, adjustment);

    const periodResult: DRFPeriodResult = {
      paymentIndex: i,
      relatedTaxBillYear: cf.relatedTaxBillYear,
      startingBalance: periodStartingBalance,
      adjustment,
      endingBalance,
      netChange,
      netRevenue,
    };

    periods.push(periodResult);

    // Check for violations
    if (endingBalance < 0) {
      violations.push({
        type: 'negative_balance',
        year: cf.relatedTaxBillYear,
        paymentIndex: i,
        balance: endingBalance,
      });
    } else if (endingBalance < minimumBalance) {
      violations.push({
        type: 'below_minimum',
        year: cf.relatedTaxBillYear,
        paymentIndex: i,
        balance: endingBalance,
        minimumRequired: minimumBalance,
      });
    }
  }

  // Calculate summary statistics
  const balances = periods.map((p) => p.endingBalance);
  const hasNegativeBalance = violations.some((v) => v.type === 'negative_balance');

  return {
    periods,
    violations,
    isViable: !hasNegativeBalance,
    summary: {
      startingBalance,
      minimumBalance,
      finalBalance: balances[balances.length - 1] ?? startingBalance,
      lowestBalance: Math.min(...balances),
      highestBalance: Math.max(...balances),
    },
  };
}

/**
 * Apply the DRF plan to a candidate's cash flows.
 *
 * This function performs the actual mutation of the candidate's data
 * based on the pre-calculated DRF plan.
 *
 * @param candidate - The candidate to update (mutated)
 * @param plan - The pre-calculated DRF plan
 *
 * @mutates candidate.cashFlows, candidate.results
 */
export function applyDRFPlan(candidate: Candidate, plan: DRFPlan): void {
  const { cashFlows, results } = candidate;

  // Apply period results to cash flows
  for (const period of plan.periods) {
    const cf = cashFlows[period.paymentIndex];
    cf.drfStartingBalance = period.startingBalance;
    cf.drfAdjustment = period.adjustment;
    cf.drfEndingBalance = period.endingBalance;
    cf.drfNetChange = period.netChange;
    cf.drfNetRevenue = period.netRevenue;
  }

  // Handle violations
  const negativeViolations = plan.violations.filter((v) => v.type === 'negative_balance');
  const belowMinViolations = plan.violations.filter((v) => v.type === 'below_minimum');

  if (negativeViolations.length > 0) {
    // Negative DRF balance - candidate is not viable
    if (!results.failureMessages) {
      results.failureMessages = [];
    }
    const years = negativeViolations.map((v) => v.year);
    results.failureMessages.push(`DRF balance is negative for years ${years.join(', ')}`);
    candidate.isProcessed = true;
    results.isViable = false;
  } else {
    // No negative violations - candidate is viable (from DRF perspective)
    // Clear any previous DRF-related failure messages and set viable
    results.failureMessages = (results.failureMessages || []).filter(
      (msg) => !msg.includes('DRF balance is negative')
    );
    results.isViable = true;
    candidate.isProcessed = true;
  }

  if (belowMinViolations.length > 0 && negativeViolations.length === 0) {
    if (!results.failureMessages) {
      results.failureMessages = [];
    }
    const years = belowMinViolations.map((v) => v.year);
    results.failureMessages.push(`DRF below minimum balance for years ${years.join(', ')}`);
  }
}

/**
 * Calculate cash flow summaries (interest obligations, available for bonds, totals).
 *
 * This is a separate step from DRF calculation that updates derived fields.
 *
 * @param cashFlows - Cash flows to update (mutated)
 */
export function calculateCashFlowSummaries(cashFlows: CashFlow[]): void {
  for (const cashFlow of cashFlows) {
    // Interest obligation is set by the revenue distributor's cascading process
    // Only initialize if not already set (preserves cascaded interest obligations)
    if (!cashFlow.interestObligation) {
      cashFlow.interestObligation = 0;
    }

    // Update available for bonds after all calculations
    cashFlow.availableForBonds = cashFlow.revenueMinusExistingDebt - cashFlow.interestObligation;

    // Calculate total principal for this period
    const totalPrincipal = cashFlow.bondPayments.reduce(
      (total, bondPayment) => total + bondPayment.principal,
      0
    );

    // Calculate total interest for this period
    const totalInterest = cashFlow.bondPayments.reduce(
      (total, bondPayment) => total + (bondPayment.totalInterest || 0),
      0
    );

    // Ensure bond payment total is accurate
    cashFlow.totalBondPayment = totalPrincipal + totalInterest;
  }
}

// ============================================================================
// LEGACY CLASS (wraps pure functions)
// ============================================================================

// Performance monitoring interface
interface PerformanceMetrics {
  executionTime: number;
  cashFlowCount: number;
  isLargeSequence: boolean;
  drfCalculationTime?: number;
  summaryCalculationTime?: number;
}

export class CashFlowCalculator {
  /**
   * Main cash flow calculation function.
   *
   * Uses the calculate/apply pipeline pattern:
   * 1. calculateDRFPlan() - pure calculation, no mutations
   * 2. applyDRFPlan() - explicit mutation of candidate
   * 3. calculateCashFlowSummaries() - update derived fields
   *
   * @param candidate - The candidate to process (mutated)
   * @param configuration - Configuration with DRF inputs
   */
  static calculate(candidate: Candidate, configuration: Configuration): void {
    const { cashFlows } = candidate;
    const drfInputs = configuration.drfInputs;

    // Validate inputs
    if (!drfInputs) {
      throw new Error(
        'DRF inputs are required for cash flow calculation. ' +
          'Ensure configuration.drfInputs is set before processing candidates.'
      );
    }

    if (!cashFlows || cashFlows.length === 0) {
      throw new Error(
        'Cash flows must be initialized before DRF calculation. ' +
          'Call CashFlowsBuilder.initializeCashFlows() first.'
      );
    }

    // Parse DRF inputs
    const inputs = {
      startingBalance: parseNumericInput(drfInputs.startingBalance),
      minimumBalance: parseNumericInput(drfInputs.minimumBalance),
    };

    // Pipeline: calculate → apply → summarize
    const plan = calculateDRFPlan(cashFlows, inputs);
    applyDRFPlan(candidate, plan);

    // Early exit if not viable (negative DRF balance)
    if (!plan.isViable) {
      return;
    }

    calculateCashFlowSummaries(cashFlows);
  }

  // NOTE: calculateDRFBalances and calculateCashFlowSummaries methods removed
  // in Phase 6 refactor. Logic moved to module-level pure functions:
  // - calculateDRFPlan() - pure DRF calculation
  // - applyDRFPlan() - explicit mutation
  // - calculateCashFlowSummaries() - summary calculation

  /**
   * Enhanced validation for cash flow sequence consistency and edge cases
   * Useful for debugging and testing - expanded for comprehensive edge case detection
   */
  static validateCashFlowSequence(cashFlows: CashFlow[]): {
    isValid: boolean;
    issues: string[];
    warnings: string[];
  } {
    const issues: string[] = [];
    const warnings: string[] = [];

    if (cashFlows.length === 0) {
      issues.push('Cash flow sequence is empty');
      return { isValid: false, issues, warnings };
    }

    for (let i = 0; i < cashFlows.length; i++) {
      const cf = cashFlows[i];
      const prevCf = i > 0 ? cashFlows[i - 1] : null;
      const nextCf = i < cashFlows.length - 1 ? cashFlows[i + 1] : null;

      // Basic required field validation
      if (cf.paymentDate === undefined || cf.paymentDate === '') {
        issues.push(`Missing payment date at index ${i}`);
      }
      if (cf.relatedTaxBillYear === undefined) {
        issues.push(`Missing tax bill year at index ${i}`);
      }

      // DRF balance continuity validation
      if (prevCf && Math.abs(cf.drfStartingBalance - prevCf.drfEndingBalance) > 0.01) {
        issues.push(
          `DRF balance discontinuity at index ${i}: expected ${prevCf.drfEndingBalance}, got ${cf.drfStartingBalance}`
        );
      }

      // DRF calculation consistency
      const expectedAdjustment = cf.revenueMinusExistingDebt - cf.totalBondPayment;
      if (Math.abs(cf.drfAdjustment - expectedAdjustment) > 0.01) {
        issues.push(
          `DRF adjustment calculation error at index ${i}: expected ${expectedAdjustment}, got ${cf.drfAdjustment}`
        );
      }

      const expectedEndingBalance = cf.drfStartingBalance + cf.drfAdjustment;
      if (Math.abs(cf.drfEndingBalance - expectedEndingBalance) > 0.01) {
        issues.push(
          `DRF ending balance calculation error at index ${i}: expected ${expectedEndingBalance}, got ${cf.drfEndingBalance}`
        );
      }

      // Bond payment validation
      const calculatedBondTotal = cf.bondPayments.reduce((sum, bp) => sum + bp.totalPayment, 0);
      if (Math.abs(cf.totalBondPayment - calculatedBondTotal) > 0.01) {
        issues.push(
          `Bond payment total mismatch at index ${i}: expected ${calculatedBondTotal}, got ${cf.totalBondPayment}`
        );
      }

      // Edge case detection - Enhanced validation

      // 1. Detect unusually large DRF swings (potential calculation errors)
      if (prevCf) {
        const drfSwing = Math.abs(cf.drfEndingBalance - prevCf.drfEndingBalance);
        const revenueBase = Math.max(
          cf.revenueMinusExistingDebt,
          prevCf.revenueMinusExistingDebt,
          1
        );
        if (drfSwing > revenueBase * 2) {
          warnings.push(
            `Large DRF swing at index ${i}: ${drfSwing.toFixed(0)} (${((drfSwing / revenueBase) * 100).toFixed(1)}% of revenue)`
          );
        }
      }

      // 2. Detect interest-only periods with principal payments (logic error)
      if (cf.isInterestOnly) {
        const totalPrincipal = cf.bondPayments.reduce((sum, bp) => sum + bp.principal, 0);
        if (totalPrincipal > 0.01) {
          issues.push(
            `Interest-only period has principal payments at index ${i}: ${totalPrincipal}`
          );
        }
      }

      // 3. Detect negative available revenue (potential constraint violation)
      if (cf.availableForBonds < -0.01) {
        warnings.push(`Negative available revenue at index ${i}: ${cf.availableForBonds}`);
      }

      // 4. Detect chronological issues (non-sequential dates)
      if (nextCf && cf.paymentDate >= nextCf.paymentDate) {
        issues.push(
          `Non-sequential payment dates at index ${i}: ${cf.paymentDate} >= ${nextCf.paymentDate}`
        );
      }

      // 5. Detect missing or inconsistent interest obligations
      if (cf.interestObligation < 0) {
        issues.push(`Negative interest obligation at index ${i}: ${cf.interestObligation}`);
      }

      // 6. Detect bond payments with missing metadata
      cf.bondPayments.forEach((bp, bpIndex) => {
        if (!bp.bondSeriesId) {
          issues.push(`Missing bond series ID at cash flow ${i}, bond payment ${bpIndex}`);
        }
        if (bp.interestRate === undefined || bp.interestRate < 0) {
          warnings.push(
            `Invalid interest rate at cash flow ${i}, bond payment ${bpIndex}: ${bp.interestRate}`
          );
        }
        if (bp.totalPayment !== bp.principal + bp.totalInterest) {
          issues.push(
            `Total payment calculation error at cash flow ${i}, bond payment ${bpIndex}: ${bp.totalPayment} !== ${bp.principal + bp.totalInterest}`
          );
        }
      });

      // 7. Detect unusual patterns that might indicate errors
      if (cf.totalBondPayment > cf.revenueMinusExistingDebt * 1.5) {
        warnings.push(
          `Bond payments exceed 150% of available revenue at index ${i}: ${cf.totalBondPayment} vs ${cf.revenueMinusExistingDebt}`
        );
      }
    }

    return {
      isValid: issues.length === 0,
      issues,
      warnings,
    };
  }

  /**
   * Get DRF statistics for a candidate
   * Useful for analysis and debugging
   */
  static getDRFStatistics(
    candidate: Candidate,
    configuration: Configuration
  ): {
    startingBalance: number;
    minimumBalance: number;
    finalBalance: number;
    lowestBalance: number;
    highestBalance: number;
    negativeBalanceYears: number[];
    belowMinimumYears: number[];
  } {
    const { cashFlows } = candidate;
    const { drfInputs } = configuration;

    // Use defensive parsing for backward compatibility with legacy formatted values
    const startingBalance = parseNumericInput(drfInputs.startingBalance);
    const minimumBalance = parseNumericInput(drfInputs.minimumBalance);

    if (!cashFlows || cashFlows.length === 0) {
      return {
        startingBalance,
        minimumBalance,
        finalBalance: startingBalance,
        lowestBalance: startingBalance,
        highestBalance: startingBalance,
        negativeBalanceYears: [],
        belowMinimumYears: [],
      };
    }

    const balances = cashFlows.map((cf) => cf.drfEndingBalance);
    const negativeBalanceYears = cashFlows
      .filter((cf) => cf.drfEndingBalance < 0)
      .map((cf) => cf.relatedTaxBillYear);
    const belowMinimumYears = cashFlows
      .filter((cf) => cf.drfEndingBalance >= 0 && cf.drfEndingBalance < minimumBalance)
      .map((cf) => cf.relatedTaxBillYear);

    return {
      startingBalance,
      minimumBalance,
      finalBalance: balances[balances.length - 1] || startingBalance,
      lowestBalance: Math.min(...balances),
      highestBalance: Math.max(...balances),
      negativeBalanceYears,
      belowMinimumYears,
    };
  }

  /**
   * Get performance metrics for cash flow calculations
   * Useful for monitoring large sequence performance
   */
  static getPerformanceMetrics(
    executionTime: number,
    cashFlowCount: number,
    additionalMetrics?: Partial<PerformanceMetrics>
  ): PerformanceMetrics {
    const isLargeSequence = cashFlowCount > 50;

    return {
      executionTime,
      cashFlowCount,
      isLargeSequence,
      ...additionalMetrics,
    };
  }

  /**
   * Comprehensive cash flow health check
   * Combines validation with DRF statistics for complete analysis
   */
  static performHealthCheck(
    candidate: Candidate,
    configuration: Configuration
  ): {
    validation: ReturnType<typeof CashFlowCalculator.validateCashFlowSequence>;
    drfStats: ReturnType<typeof CashFlowCalculator.getDRFStatistics>;
    recommendations: string[];
  } {
    const validation = CashFlowCalculator.validateCashFlowSequence(candidate.cashFlows);
    const drfStats = CashFlowCalculator.getDRFStatistics(candidate, configuration);
    const recommendations: string[] = [];

    // Generate recommendations based on analysis
    if (validation.warnings.length > 0) {
      recommendations.push('Review cash flow warnings for potential optimization opportunities');
    }

    if (drfStats.lowestBalance < drfStats.minimumBalance * 0.5) {
      recommendations.push('Consider increasing DRF starting balance or reducing bond commitments');
    }

    if (drfStats.negativeBalanceYears.length > 0) {
      recommendations.push('Critical: Address negative DRF balances immediately');
    }

    const drfVolatility = drfStats.highestBalance - drfStats.lowestBalance;
    if (drfVolatility > drfStats.startingBalance * 2) {
      recommendations.push(
        'High DRF volatility detected - consider smoothing revenue or bond payments'
      );
    }

    if (candidate.cashFlows.length > 100) {
      recommendations.push('Large cash flow sequence - monitor performance metrics');
    }

    return {
      validation,
      drfStats,
      recommendations,
    };
  }
}
